import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { callApiWithoutAuth } from "@/dataprovider/misc-apis";
import { resolveAuthlessPrintId } from "./resolve-print-id";
import { selectFirstPrintRow } from "./select-print-row";

export type UseAuthlessPrintRowOptions<T> = {
  location: string;
  /** Build the path id segment from the resolved id. Defaults to the raw id. */
  buildId?: (resolvedId: string) => string;
  /** Extract the print row from the API response body. Defaults to first of `rows`. */
  selectRow?: (body: unknown) => T | null | undefined;
  /**
   * When set, read this hash/search query param instead of the route `:id`
   * (e.g. `receiptId` for Sabil / FMB / Niyaaz receipt-layout prints).
   */
  idFromSearchParam?: string;
  /** Full override for id resolution; wins over route and `idFromSearchParam`. */
  resolveId?: () => string | null;
};

export type AuthlessPrintRowState<T> = {
  data: T | null;
  error: boolean;
};

const defaultBuildId = (resolvedId: string) => `print/${resolvedId}`;

export function useAuthlessPrintRow<T>({
  location,
  buildId = defaultBuildId,
  selectRow = selectFirstPrintRow,
  idFromSearchParam,
  resolveId,
}: UseAuthlessPrintRowOptions<T>): AuthlessPrintRowState<T> {
  const { id: routeId } = useParams<{ id: string }>();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState(false);

  const id = resolveAuthlessPrintId({
    routeId,
    idFromSearchParam,
    resolveId,
  });

  const buildIdRef = useRef(buildId);
  const selectRowRef = useRef(selectRow);
  buildIdRef.current = buildId;
  selectRowRef.current = selectRow;

  useEffect(() => {
    if (!id) {
      setError(true);
      setData(null);
      return;
    }

    setError(false);

    callApiWithoutAuth({ location, method: "GET", id: buildIdRef.current(id) })
      .then((res) => {
        const row = selectRowRef.current(res.data);
        if (row == null) {
          setError(true);
          setData(null);
        } else {
          setData(row);
        }
      })
      .catch(() => {
        setError(true);
        setData(null);
      });
  }, [id, location]);

  return { data, error };
}
